import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllSeatAssignments, clearAllSeatAssignments, getSettings } from '../utils/db';
import SeatQRCode from '../components/SeatQRCode';
import SettingsForm from '../components/SettingsForm';
import { SeatAssignment, SeatConfig, SystemSettings } from '../types';

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState<SeatAssignment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isResetModalOpen, setIsResetModalOpen] = useState<boolean>(false);
  const [isPrintMode, setIsPrintMode] = useState<boolean>(false);
  const [settings, setSettings] = useState<SystemSettings>({ totalSeats: 30 });
  
  // 席の設定
  const seatConfig: SeatConfig = {
    totalSeats: settings.totalSeats,
    isTableLayout: false // 連番席のため、テーブルレイアウトはfalse
  };
  
  useEffect(() => {
    const initPage = async () => {
      setLoading(true);
      try {
        // システム設定を読み込む
        const settingsData = await getSettings();
        setSettings(settingsData);
        
        // 席割り当てデータを読み込む
        const data = await getAllSeatAssignments();
        setAssignments(data);
        setError(null);
      } catch (err) {
        console.error('データの読み込みに失敗しました:', err);
        setError('データの読み込みに失敗しました');
      } finally {
        setLoading(false);
      }
    };
    
    initPage();
  }, []);
  
  const handleReset = async () => {
    try {
      setLoading(true);
      await clearAllSeatAssignments();
      setAssignments([]);
      setIsResetModalOpen(false);
      setError(null);
    } catch (err) {
      console.error('席割り当てのリセットに失敗しました:', err);
      setError('席割り当てのリセットに失敗しました');
    } finally {
      setLoading(false);
    }
  };
  
  const handlePrintQRCodes = () => {
    setIsPrintMode(true);
    setTimeout(() => {
      window.print();
      setIsPrintMode(false);
    }, 500);
  };
  
  const handleReturnHome = () => {
    navigate('/');
  };
  
  const handleSettingsSaved = (newSettings: SystemSettings) => {
    setSettings(newSettings);
  };
  
  // 印刷モード用のQRコードリスト生成
  const renderPrintableQRCodes = () => {
    const qrCodes = [];
    
    for (let seatNumber = 1; seatNumber <= seatConfig.totalSeats; seatNumber++) {
      qrCodes.push(
        <div key={seatNumber} className="qrcode-print-item">
          <SeatQRCode 
            seatNumber={seatNumber} 
            config={seatConfig}
          />
        </div>
      );
    }
    
    return (
      <div className="qrcode-print-container">
        {qrCodes}
      </div>
    );
  };
  
  // 席割り当てテーブル
  const renderAssignmentTable = () => {
    return (
      <table className="assignments-table">
        <thead>
          <tr>
            <th>席番号</th>
            <th>名前</th>
            <th>抽選日時</th>
          </tr>
        </thead>
        <tbody>
          {assignments.length === 0 ? (
            <tr>
              <td colSpan={3} className="no-data">席の割り当てはまだありません</td>
            </tr>
          ) : (
            assignments.map((assignment) => (
              <tr key={assignment.id}>
                <td>{assignment.seatNumber}</td>
                <td>{assignment.name}</td>
                <td>{new Date(assignment.timestamp).toLocaleString()}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    );
  };
  
  // リセット確認モーダル
  const renderResetModal = () => {
    if (!isResetModalOpen) return null;
    
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <h3>席割り当てのリセット</h3>
          <p>全ての席割り当てをリセットします。この操作は元に戻せません。</p>
          <p>続行しますか？</p>
          
          <div className="modal-actions">
            <button 
              onClick={() => setIsResetModalOpen(false)}
              className="cancel-button"
            >
              キャンセル
            </button>
            <button 
              onClick={handleReset}
              className="reset-button"
            >
              リセット
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  if (isPrintMode) {
    return renderPrintableQRCodes();
  }
  
  return (
    <div className="admin-page">
      <div className="container">
        <header>
          <h1>管理者ページ</h1>
        </header>
        
        <main>
          {loading ? (
            <div className="loading">読み込み中...</div>
          ) : (
            <>
              <div className="admin-actions">
                <button 
                  onClick={handlePrintQRCodes}
                  className="print-button"
                >
                  席QRコードを印刷
                </button>
                <button 
                  onClick={() => setIsResetModalOpen(true)}
                  className="reset-button"
                >
                  席割り当てをリセット
                </button>
                <button 
                  onClick={handleReturnHome}
                  className="return-button"
                >
                  ホームに戻る
                </button>
              </div>
              
              {error && <div className="error-message">{error}</div>}
              
              <div className="admin-settings">
                <SettingsForm 
                  currentSettings={settings}
                  onSettingsSaved={handleSettingsSaved}
                />
              </div>
              
              <h2>席割り当て一覧</h2>
              {renderAssignmentTable()}
            </>
          )}
        </main>
      </div>
      
      {renderResetModal()}
    </div>
  );
};

export default AdminPage; 